import { useState } from "react";
import { Loader2, CheckCircle } from "lucide-react";
import { BTN_PRIMARY } from "./buttonStyles";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { docsAPI, fmtINR } from "@/lib/documents";
import { PAYMENT_METHODS, REFERENCE_REQUIRED, methodLabel } from "@/components/pms/AdvancePaymentInput";

/** Step 3 · collect the outstanding balance onto the folio and issue a receipt. */
export function PaymentStep({ folioId, balance, onPaid }) {
  const [amount, setAmount] = useState(balance > 0 ? balance.toFixed(2) : "");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount greater than zero"); return; }
    if (REFERENCE_REQUIRED.has(method) && !reference.trim()) { toast.error(`Reference number is required for ${methodLabel(method)}`); return; }
    setBusy(true);
    try {
      const { data: pay } = await api.post("/pms/payments/", { folio_id: folioId, method, amount: amt, reference: reference.trim() || null });
      let receipt = null;
      try { receipt = await docsAPI.createReceipt({ payment_id: pay.id, receipt_type: "room" }); } catch { /* receipt optional */ }
      toast.success(`${fmtINR(amt)} collected`);
      onPaid?.({ payment: pay, receipt, amount: amt });
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="payment-step">
      <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 flex items-center justify-between">
        <span className="text-[13px] font-semibold text-[#78530E]">Balance to collect</span>
        <span className="mono text-[18px] font-bold text-[#78530E]" data-testid="payment-balance">{fmtINR(balance)}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-[12px]">Amount</Label>
          <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount" />
        </div>
        <div>
          <Label className="text-[12px]">Method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
            <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[12px]">Reference {REFERENCE_REQUIRED.has(method) && <span className="text-hocp-error">*</span>}</Label>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Txn / UTR / approval code" data-testid="payment-reference" />
        </div>
      </div>
      <button type="button" onClick={submit} disabled={busy} className={BTN_PRIMARY} data-testid="payment-confirm">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={15} strokeWidth={2} />}Confirm Payment
      </button>
    </div>
  );
}
